import { useAtomValue } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import type { Tenant } from '@core/Entities/App/Tenants.entity';
import type { Locale } from '@core/Entities/Locale/Locale.entity';
import type { FilterKey } from '@core/Entities/Search/QueryParams.entity';
import { getSlugParamFromFilter } from '@core/Services/Search/Utils/GetSlugParamFromFilter';
import { getFilterFromArray } from '@core/Utils/SearchFilters/GetFilterFromArray';

import { createFiltersLinkForFilterValue } from 'Utils/SearchFilters/CreateFiltersLinkForFilterValue';
import { createFiltersLinkForRangeFilterValue } from 'Utils/SearchFilters/CreateFiltersLinkForRangeFilterValue';
import { shouldDisplayFilterOptionLink } from 'Utils/SearchFilters/ShouldDisplayFilterOptionLink';

//TODO: Seems like we can refactor this to not bother to run if the filter is not a path param.
//Instead of getting all the way to line 63
export const useFilterLink = ({
  filterId,
  partnerFilterId,
  locale,
  isPlpFiltersSeoEnabled,
  tenant,
}: {
  filterId: FilterKey;
  partnerFilterId?: FilterKey;
  locale: Locale;
  isPlpFiltersSeoEnabled: boolean;
  tenant: Tenant | undefined;
}) => {
  const filtersList = useAtomValue(filtersListAtom);
  const filter = getFilterFromArray(filterId, filtersList);
  const partnerFilter = partnerFilterId && getFilterFromArray(partnerFilterId, filtersList);
  const selectedOptions = new Set(filter?.value || []);
  const selected = new Set(selectedOptions);

  const createFlrtLinkArgs = {
    filters: filtersList,
    locale,
    tenant,
  };
  const createLinkForValue = partnerFilterId
    ? createFiltersLinkForRangeFilterValue({
        ...createFlrtLinkArgs,
        filterIds: [filterId, partnerFilterId],
      })
    : createFiltersLinkForFilterValue({
        ...createFlrtLinkArgs,
        filterId,
      });

  const extraFacetsWithActiveFilters = filtersList
    .map(filterItem => getSlugParamFromFilter(filterItem.id))
    .filter(
      filterItem => filterItem !== 'make' && filterItem !== 'model' && filterItem !== undefined,
    );
  const numExtraFacetsWithActiveFilters = [...new Set(extraFacetsWithActiveFilters)].length;

  const getLinkForFilterValue = ({
    filterValue,
    resultCount,
  }: {
    filterValue: string;
    resultCount: number;
  }): {
    href: string;
    nofollow: boolean;
  } | null => {
    if (!isPlpFiltersSeoEnabled) return null;

    let isSelected = selected.has(filterValue);
    let hasOptSelected = selectedOptions.size > 0;
    //Things work a little different for range filters
    if (partnerFilter) {
      isSelected = selected.has(filterValue) && partnerFilter.value === filterValue;
      // want links to appear even if one is active
      hasOptSelected = false;
    }

    const shouldDisplay = shouldDisplayFilterOptionLink({
      numExtraFacetsWithActiveFilters,
      isSelected,
      resultCount,
      categoryHasOptionSelected: hasOptSelected,
    });

    if (shouldDisplay === 'omit') return null;

    const href = createLinkForValue(filterValue);

    const hasFilterIdAsSearchParam = new URLSearchParams(href.split('?')[1] ?? '').has(filterId);
    if (hasFilterIdAsSearchParam) return null;

    if (filterId === 'color' && (filterValue === 'other' || filterValue === 'othercolor'))
      return null;

    const hrefWithoutSearchParams = href.split('?')[0];

    return {
      href: hrefWithoutSearchParams,
      nofollow: shouldDisplay === 'no-follow',
    };
  };

  return {
    /**
     * Given a filter value and the amount of results it has,
     * returns a link that adds or removes the filter value.
     */
    getLinkForFilterValue,
  };
};
